/**
 * @file 请求包装
 * @author  fangsimin
 * @date    2019-01-14 15:14:23
 * @last Modified by    fangsimin
 * @last Modified time  2019-01-14 20:02:45
 */

import axios from 'axios';
import qs from 'qs';
import {captrueApiException} from '@/plugins/weirwood';
import Cookies from 'js-cookie';
// 灰度发布标记
const handleIsgu = (options) => {
    const isgu = Cookies.get('isgu');
    if(isgu !== undefined && options['headers']) {
         options['headers'].isgu = isgu;
    }
};

axios.interceptors.request.use(config => {
    let url = config.url;
    // get参数编码
    if ((config.method === 'get' || config.method === 'delete') && config.params) {
        url += '?';
        let keys = Object.keys(config.params);
        for (let key of keys) {
            if (config.params[key] !== undefined) {
                url += `${key}=${encodeURIComponent(config.params[key])}&`;
            }
        }
        url = url.substring(0, url.length - 1);
        config.params = {};
    }
    config.url = url;
    return config;
});

/**
 * 通过 Performance API 分析超时请求卡在哪个网络阶段
 * @param {Object} response - axios 超时错误对象
 * @returns {Object|null} { timeoutType, details, timeout }
 *   timeoutType 取值:
 *     DNS_TIMEOUT           - DNS 解析超时
 *     TCP_TIMEOUT           - TCP 连接超时
 *     SSL_TIMEOUT           - SSL 握手超时
 *     SERVER_RESPONSE_TIMEOUT - 请求已发出，等待服务端响应超时
 *     DOWNLOAD_TIMEOUT      - 响应体下载超时
 *     ACCUMULATED_TIMEOUT   - 各阶段累积导致超时
 *     REQUEST_NOT_SENT      - 未找到 Performance 条目，请求可能未发出
 */
function getTimeoutDetail(response) {
    const url = response && response.config && response.config.url;
    if (!url) return null;

    const entries = performance.getEntriesByType('resource');
    const entry = entries.find(e => e.name === url || e.name.includes(url));

    const timeout = (response.config && response.config.timeout) || 30000;
    let timeoutType = 'UNKNOWN';
    let details = {};

    if (entry) {
        const dns = entry.domainLookupEnd - entry.domainLookupStart;
        const tcp = entry.connectEnd - entry.connectStart;
        const ssl = entry.secureConnectionStart > 0
            ? entry.connectEnd - entry.secureConnectionStart
            : 0;
        const requestSent = entry.requestStart > 0;
        const responseReceived = entry.responseStart > 0;
        // 超时中断时服务端尚未返回数据，responseStart 为 0，用 -1 标记"未收到服务端响应"
        const serverWait = responseReceived ? entry.responseStart - entry.requestStart : -1;
        const contentDownload = responseReceived ? entry.responseEnd - entry.responseStart : -1;

        if (dns > timeout) timeoutType = 'DNS_TIMEOUT';
        else if (tcp > timeout) timeoutType = 'TCP_TIMEOUT';
        else if (ssl > timeout) timeoutType = 'SSL_TIMEOUT';
        // 请求已发出但服务端未返回首字节即被中断（最常见的超时场景）
        else if (requestSent && !responseReceived) timeoutType = 'SERVER_RESPONSE_TIMEOUT';
        else if (serverWait > timeout) timeoutType = 'SERVER_RESPONSE_TIMEOUT';
        else if (contentDownload > timeout) timeoutType = 'DOWNLOAD_TIMEOUT';
        else timeoutType = 'ACCUMULATED_TIMEOUT';

        details = {
            dns: dns + 'ms',
            tcp: tcp + 'ms',
            ssl: ssl + 'ms',
            serverWait: serverWait === -1 ? `未收到服务端响应,responseStart=${entry.responseStart}` : serverWait + 'ms',
            contentDownload: contentDownload === -1 ? `未收到服务端响应,responseStart=${entry.responseStart}` : contentDownload + 'ms'
        };
    } else {
        // 无 Performance 条目，可能请求在 DNS 之前就被中断或浏览器未记录
        timeoutType = 'REQUEST_NOT_SENT';
        details = { reason: 'No performance entry found' };
    }

    return { timeoutType, details, timeout };
}

/**
 * 请求失败时的初步处理回调
 * @param  {Object} response 返回
 * @return  {Object} response 返回
 */
function errorState(response) {
    try {
        const code = response && response.data && response.data.code;
        const status = response && response.status;
        const codes = response && response.code && response.code;
        const isEffectiveResponse = +status === 200 && (+code === 0 || +code === 101);
        if (codes === 'ECONNABORTED') {
            const timeoutDetail = getTimeoutDetail(response);
            if (timeoutDetail && response.config) {
                response.config.timeoutDetail = timeoutDetail;
            }
        }
        if (!isEffectiveResponse || codes === 'ECONNABORTED') {
            response && captrueApiException(response);
        }
    }
    catch (err) {
        console.log(err);
    }
    if (response && (response.status === 200 || response.status === 304 || response.status === 400)) {
        return response;
    } else if (response && +response.status === 401) {
        const url = (response.headers && response.headers['location']) || '';
        if (url) {
            location.href = url;
        }
        return null;
    } else {
        return null;
    }
}

/**
 * 请求成功返回时的初步处理回调
 * @param  {Object} response 返回
 * @return {boolean} 返回服务端的code是否为0
 */
function successState(response) {
    // 101状态码用于处理登录失败的特殊提示
    if (response.data && (+response.data.code === 0 || +response.data.code === 101)) {
        return true;
    } else {
        return false;
    }
    // 一判断后端返回的错误码
    // if (res.data.errCode == '000002') {
    //   console.log(res.data.errDesc || '网络异常')
    // } else if (res.data.errCode != '000002' && res.data.errCode != '000000') {
    //   console.log(res.data.errDesc || '网络异常')
    // }
}

/**
 * 文件下载
 * @param {ArrayBuffer} response
 */
function downloadFile(data, fileName, type) {
    const url = window.URL.createObjectURL(new Blob([data], {type: type}));
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export default (opts, data) => {
    if (!opts.method) {
        opts.method = 'get';
    }
    // 公共参数
    let defaultParam = {
        // 'testParam': ''
    };
    // http默认配置
    let httpDefaultOpts = {
        baseURL: opts.baseURL,
        method: opts.method, // 基础 url 前缀
        url: opts.url, // 请求的地址
        timeout: opts.timeout || 30000 // 超时时间, 单位毫秒
    };
    const randomString = [...Array(16)].map(() => Math.random().toString(36)[2]).join('');
    if (opts.method === 'get' || opts.method === 'delete') {
        httpDefaultOpts['params']
            = Object.assign(defaultParam, opts.params, data);
        httpDefaultOpts['headers'] = {
            // 'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=UTF-8',
             Traceid: randomString
        };
    } else {
        // 处理formdata格式的上传
        if (opts['ajaxType'] && opts['ajaxType'] === 'upload') {
            httpDefaultOpts['data'] = data;
        } else {
            httpDefaultOpts['data']
                = opts.qs
                    ? qs.stringify(Object.assign(defaultParam, opts.data, data))
                    : Object.assign(defaultParam, opts.data, data);
        }
        httpDefaultOpts['headers'] = {
            // 'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': opts.contentType || 'application/x-www-form-urlencoded; charset=UTF-8',
             Traceid: randomString
        };
        httpDefaultOpts['transformRequest'] = opts.contentType ? opts.transformRequest || [] : [function (data) {
            let ret = '';
            /* eslint-disable-next-line */
            for (let it in data) {
                ret += encodeURIComponent(it) + '=' + encodeURIComponent(data[it]) + '&';
            }
            return ret;
        }];
    }
    handleIsgu(httpDefaultOpts);
    if (opts.type === 'download') {
        httpDefaultOpts['responseType'] = 'arraybuffer';
        // httpDefaultOpts['headers'] = {'Content-Type': 'application/csv; charset=UTF-8'};

        return new Promise((resolve, reject) => {
            axios(httpDefaultOpts).then(response => {
                if (+response.status === 200) {
                    const header = response.headers;
                    const fileName = (
                        header['content-disposition']
                        && header['content-disposition'].split('filename=')[1]
                    ) || 'filename';
                    downloadFile(response.data, fileName, opts.fileType);
                    resolve(true);
                } else {
                    if (+response.status === 401) {
                        this.$message.error(this.$i18n.t('message.permissionDenied'));
                        setTimeout(() => {
                            window.location.replace('/adx-dc/logout.do');
                        }, 500);
                    }
                    reject(response);
                }
            }).catch(response => {
                errorState(response);
                reject(response);
            });
        });
    } else {
        httpDefaultOpts['responseType'] = 'json';
    }
    opts.startTime = new Date().getTime();
    return new Promise(function (resolve, reject) {
        axios(httpDefaultOpts).then(response => {
            response.data.wait_seconds = (new Date().getTime() - opts.startTime) / 1000;
            if (successState(response)) {
                resolve(response.data);
            } else {
                errorState(response);
                reject(response);
            }
        }).catch(response => {
            if(response.code && response.code === 'ECONNABORTED') {
                errorState(response);
                reject(response);
            } else {
                errorState(response.response);
                reject(response.response);
            }
        });
    });
};
